/**
 * Utility functions for JWT token handling
 */

export interface DecodedToken {
  customerId?: string;
  employeeID?: string;
  employerId?: string;
  userId?: string;
  userIdentifier?: string;
  email?: string;
  role?: string;
  adminId?: string;
  companyId?: string;
  permissions?: string[];
  iat?: number;
  exp?: number;
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the token signature. Verification should be done server-side.
 */
export function decodeJWT(token: string): DecodedToken | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // ✅ FIXED: Check if payload exists
    if (!payload) {
      return null;
    }
    
    // Add padding if needed (base64url decoding)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    
    // Decode from base64
    const decoded = atob(padded);
    
    // Parse JSON
    return JSON.parse(decoded) as DecodedToken;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Get companyId from JWT token stored in localStorage or sessionStorage
 */
export function getCompanyIdFromToken(): string | null {
  try {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    if (!token) {
      return null;
    }

    const decoded = decodeJWT(token);
    return decoded?.companyId || null;
  } catch (error) {
    console.error('Error extracting companyId from token:', error);
    return null;
  }
}

/**
 * Get employerId from JWT token stored in localStorage or sessionStorage
 */
export function getEmployerIdFromToken(): string | null {
  try {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    if (!token) {
      return null;
    }

    const decoded = decodeJWT(token);
    return decoded?.employerId || decoded?.userId || null;
  } catch (error) {
    console.error('Error extracting employerId from token:', error);
    return null;
  }
}

